<!-- src/components/MessageCopyButton.vue - OPRAVENÁ VERZE -->
<template>
  <!-- Dialog pro výběr formátu emailu -->
  <Teleport to="body">
    <div
      v-if="showEmailDialog"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      @click="showEmailDialog = false"
    >
      <div
        class="bg-white rounded-lg p-4 w-full max-w-sm shadow-xl"
        @click.stop
      >
        <h3 class="text-base md:text-lg font-bold mb-3">Vyberte formát emailu</h3>
        <div class="flex flex-col gap-2">
          <button
            @click="sendPlainTextEmail"
            class="px-3 py-2.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-left"
          >
            <div class="font-semibold text-sm">📧 Plain Text</div>
            <div class="text-xs opacity-90">Otevře emailovou aplikaci s prostým textem</div>
          </button>
          <button
            @click="createGmailDraft"
            class="px-3 py-2.5 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-left"
          >
            <div class="font-semibold text-sm">📬 Vytvořit draft v Gmailu</div>
            <div class="text-xs opacity-90">Otevře Gmail s formátovaným HTML obsahem</div>
          </button>
          <button
            @click="showEmailDialog = false"
            class="px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors text-sm"
          >
            Zrušit
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <div v-if="showCopyButton" class="absolute top-1 right-1 flex gap-1 z-10">
    <!-- Kopírovat do schránky -->
    <button
      @click="copyToClipboard"
      :class="[
        'p-1.5 transition-all duration-300 group rounded',
        copySuccess
          ? 'text-green-500'
          : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
      ]"
      :title="
        copySuccess ? 'Zkopírováno!' : 'Kopírovat formátovaný HTML pro email'
      "
    >
      <!-- Ikona schránky nebo checkmark -->
      <svg
        v-if="!copySuccess"
        class="w-4 h-4 transition-transform group-hover:scale-110"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
        />
      </svg>
      <svg
        v-else
        class="w-4 h-4 text-green-500 animate-in"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M5 13l4 4L19 7"
        />
      </svg>

      <!-- Tooltip -->
      <span
        class="absolute right-full mr-2 py-1 px-2 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none"
      >
        {{ copySuccess ? "Zkopírováno!" : "Kopírovat HTML pro email" }}
      </span>
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import MarkdownIt from "markdown-it";
import DOMPurify from "dompurify";
import { hasGmailAuth, getGmailAccessToken, requestGmailAuth, createGmailDraft as createDraft } from "../../utils/gmailApi.js";

const props = defineProps({
  text: String,
  sources: Array,
});

const copySuccess = ref(false);
const showCopyButton = ref(false);
const showEmailDialog = ref(false);

// Vlastní MarkdownIt instance - stejná konfigurace jako v MessageContent
const md = new MarkdownIt({
  html: true,
  breaks: true,
  linkify: true,
  typographer: true,
});

// Po načtení komponenty zobrazíme tlačítko s animací
onMounted(() => {
  setTimeout(() => {
    showCopyButton.value = true;
  }, 500);
});

// KLÍČOVÁ ZMĚNA: Zpracovat text stejně jako MessageContent.vue
const formattedHtml = computed(() => {
  if (!props.text) return "";

  if (typeof props.text === "object") {
    console.error("Received object instead of string for text:", props.text);
    return "Chyba: Neplatný formát textu";
  }

  try {
    let html = md.render(props.text);

    // STEJNÉ EMAIL OPRAVY JAKO V MessageContent.vue
    // 1. Oprav chybně formátované email odkazy po markdown-it
    html = html.replace(
      /<a href="([^"]*@[^"]*)"[^>]*>([^<]*@[^<]*)"?[^<]*<\/a>/g,
      '<a href="mailto:$1">$2</a>'
    );

    // 2. Oprav email odkazy bez mailto:
    html = html.replace(/<a href="([^"]*@[^"]*)">/g, '<a href="mailto:$1">');

    // 3. Oprav duplicitní mailto:
    html = html.replace(/mailto:mailto:/g, "mailto:");

    // 4. Oprav situace kde je email adresa rozbita uvnitř tagu
    html = html.replace(
      /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})"[^>]*>/g,
      (match, email) => `<a href="mailto:${email}">${email}</a>`
    );

    // 5. Zajisti, že všechny samostatné email adresy budou klikatelné
    html = html.replace(
      /\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b(?![^<]*>)/g,
      '<a href="mailto:$1">$1</a>'
    );

    // Keywords highlighting - stejně jako MessageContent
    const keywords = [
      "HOTJET",
      "Köhler",
      "tepelná čerpadla",
      "tepelné čerpadlo",
      "vzduch-voda",
      "země-voda",
      "voda-voda",
    ];

    keywords.forEach((keyword) => {
      const regex = new RegExp(`\\b(${keyword})\\b(?![^<]*>|[^<]*<\/a>)`, "gi");
      html = html.replace(
        regex,
        '<mark class="bg-yellow-200 px-1 rounded">$1</mark>'
      );
    });

    // Emoji replacement - stejně jako MessageContent
    html = replaceEmojiShortcodes(html);

    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "em",
        "ul",
        "ol",
        "li",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "blockquote",
        "code",
        "pre",
        "a",
        "table",
        "thead",
        "tbody",
        "tr",
        "th",
        "td",
        "sup",
        "sub",
        "mark",
        "del",
        "ins",
        "span",
      ],
      ALLOWED_ATTR: ["href", "target", "rel", "class", "style", "title"],
      KEEP_CONTENT: true,
      ADD_ATTRIBUTES: {
        a: {
          target: "_blank",
          rel: "noopener noreferrer",
        },
      },
      ALLOWED_URI_REGEXP:
        /^(?:(?:https?|ftp|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
      SANITIZE_NAMED_PROPS: false,
      FIX_NESTED_LINKS: false,
    });
  } catch (error) {
    console.error("Error in formattedHtml:", error);
    return "Chyba při formátování textu";
  }
});

// Emoji replacement function - stejná jako MessageContent
function replaceEmojiShortcodes(html) {
  const emojiMap = {
    ":fire:": "🔥",
    ":check:": "✅",
    ":x:": "❌",
    ":bulb:": "💡",
    ":money:": "💰",
    ":house:": "🏠",
    ":sun:": "☀️",
    ":snowflake:": "❄️",
    ":chart:": "📊",
    ":wrench:": "🔧",
    ":phone:": "📞",
    ":email:": "📧",
    ":rocket:": "🚀",
    ":heart:": "❤️",
    ":thumbsup:": "👍",
    ":warning:": "⚠️",
    ":info:": "ℹ️",
    ":question:": "❓",
    ":ok:": "👌",
    ":star:": "⭐",
    ":lightning:": "⚡",
    ":checkmark:": "✔️",
    ":euro:": "€",
    ":thermometer:": "🌡️",
    ":droplet:": "💧",
    ":wind:": "💨",
    ":earth:": "🌍",
    ":gear:": "⚙️",
    ":tools:": "🛠️",
    ":ruler:": "📏",
    ":handshake:": "🤝",
    ":calculator:": "🧮",
    ":calendar:": "📅",
    ":money_bag:": "💰",
    ":zap:": "⚡",
  };

  Object.entries(emojiMap).forEach(([shortcode, emoji]) => {
    const escapedShortcode = shortcode.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escapedShortcode, "g");
    html = html.replace(regex, emoji);
  });

  return html;
}

// Hlavní funkce pro kopírování HTML do emailu - OPRAVENÁ
async function copyToClipboard() {
  try {
    // Vytvoř čistý HTML pro email bez zdrojů a citací
    const cleanEmailHtml = createEmailHtml(formattedHtml.value);
    const plainTextFallback = htmlToPlainText(formattedHtml.value);


    // Pokus o moderní Clipboard API s HTML podporou
    if (navigator.clipboard && window.isSecureContext && window.ClipboardItem) {
      try {
        const clipboardItem = new ClipboardItem({
          "text/html": new Blob([cleanEmailHtml], { type: "text/html" }),
          "text/plain": new Blob([plainTextFallback], { type: "text/plain" }),
        });

        await navigator.clipboard.write([clipboardItem]);
        showCopyFeedback();
        return;
      } catch (error) {
        console.warn("Moderní Clipboard API selhalo, zkouším fallback:", error);
      }
    }

    // Fallback pro starší prohlížeče
    await fallbackCopyHtml(cleanEmailHtml, plainTextFallback);
    showCopyFeedback();
  } catch (error) {
    console.error("Chyba při kopírování do schránky:", error);
    alert(
      "Nepodařilo se zkopírovat obsah do schránky. Zkuste to prosím znovu."
    );
  }
}

// Vytvoří email-ready HTML s inline styly - OPRAVENÁ VERZE
function createEmailHtml(html) {
  if (!html) return "";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // NEJDŘÍV oprav zdvojené emaily
  tempDiv.innerHTML = tempDiv.innerHTML.replace(
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\1+/g,
    "$1"
  );

  // ODSTRAŇ všechny citace a reference čísla
  // Odstraň [1], [2], atd.
  tempDiv.innerHTML = tempDiv.innerHTML.replace(/\[\d+\]/g, "");

  // Odstraň sup elementy s čísly
  const sups = tempDiv.querySelectorAll("sup");
  sups.forEach((sup) => {
    if (/^\[\d+\]$/.test(sup.textContent.trim())) {
      sup.remove();
    }
  });

  // Aplikuj inline styly pro email klienty
  const wrapper = document.createElement("div");
  wrapper.style.fontFamily =
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif";
  wrapper.style.fontSize = "14px";
  wrapper.style.lineHeight = "1.6";
  wrapper.style.color = "#333333";
  wrapper.style.maxWidth = "100%";
  wrapper.style.width = "100%";

  // Oprav odkazy s inline styly
  const links = tempDiv.querySelectorAll("a");
  links.forEach((link) => {
    link.style.color = "#1a73e8";
    link.style.textDecoration = "underline";
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    // OPRAV zdvojené emaily v odkazech
    const href = link.getAttribute("href");
    const text = link.textContent;

    if (href && href.startsWith("mailto:")) {
      const email = href.replace("mailto:", "");
      // Pokud text obsahuje email vícekrát, oprav to
      if (text.includes(email + email)) {
        link.textContent = email;
      }
      // Pokud text není email, nastav ho na email
      if (!text.includes("@")) {
        link.textContent = email;
      }
    }
  });

  // Oprav tučný text
  const bolds = tempDiv.querySelectorAll("strong, b");
  bolds.forEach((bold) => {
    bold.style.fontWeight = "bold";
    bold.style.color = "#1a1a1a";
  });

  // Oprav zvýrazněné texty (mark)
  const marks = tempDiv.querySelectorAll("mark");
  marks.forEach((mark) => {
    mark.style.backgroundColor = "#fff3cd";
    mark.style.padding = "2px 4px";
    mark.style.borderRadius = "3px";
    mark.style.color = "#856404";
  });

  // Oprav seznamy
  const lists = tempDiv.querySelectorAll("ul, ol");
  lists.forEach((list) => {
    list.style.marginLeft = "20px";
    list.style.marginBottom = "16px";
  });

  // Oprav odstavce
  const paragraphs = tempDiv.querySelectorAll("p");
  paragraphs.forEach((p) => {
    p.style.marginBottom = "12px";
    p.style.marginTop = "0";
  });

  // Oprav nadpisy
  const headings = tempDiv.querySelectorAll("h1, h2, h3, h4, h5, h6");
  headings.forEach((heading) => {
    heading.style.fontWeight = "bold";
    heading.style.marginTop = "24px";
    heading.style.marginBottom = "12px";
    heading.style.color = "#1a1a1a";
  });

  // OPRAV TABULKY - responzivní pro email
  const tables = tempDiv.querySelectorAll("table");
  tables.forEach((table) => {
    table.style.borderCollapse = "collapse";
    table.style.width = "100%";
    table.style.maxWidth = "100%";
    table.style.marginBottom = "16px";
    table.style.tableLayout = "auto";
    table.style.fontSize = "13px";

    const cells = table.querySelectorAll("th, td");
    cells.forEach((cell) => {
      cell.style.border = "1px solid #ddd";
      cell.style.padding = "6px 8px";
      cell.style.textAlign = "left";
      cell.style.verticalAlign = "top";
      cell.style.wordWrap = "break-word";
      cell.style.wordBreak = "break-word";
    });

    const headers = table.querySelectorAll("th");
    headers.forEach((th) => {
      th.style.backgroundColor = "#f8f9fa";
      th.style.fontWeight = "bold";
    });
  });

  wrapper.appendChild(tempDiv);

  // NEPŘIDÁVEJ zdroje - jen čistý obsah pro email
  return wrapper.outerHTML;
}

// Funkce pro konverzi HTML na čistý text - OPRAVENÁ
function htmlToPlainText(html) {
  if (!html) return "";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // ODSTRAŇ citace a reference před konverzí
  tempDiv.innerHTML = tempDiv.innerHTML.replace(/\[\d+\]/g, "");

  const sups = tempDiv.querySelectorAll("sup");
  sups.forEach((sup) => {
    if (/^\[\d+\]$/.test(sup.textContent.trim())) {
      sup.remove();
    }
  });

  // OPRAV zdvojené emaily
  tempDiv.innerHTML = tempDiv.innerHTML.replace(
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\1+/g,
    "$1"
  );

  // Nahraď odkazy POUZE jejich textem
  const links = tempDiv.querySelectorAll("a");
  links.forEach((link) => {
    const href = link.getAttribute("href");
    let text = link.textContent;

    if (href && href.startsWith("mailto:")) {
      const email = href.replace("mailto:", "");
      // Oprav zdvojené emaily v textu
      if (text.includes(email + email)) {
        text = email;
      } else if (!text.includes("@")) {
        text = email;
      }
      link.replaceWith(document.createTextNode(text));
    } else {
      link.replaceWith(document.createTextNode(text));
    }
  });

  // Ošetř markdown formátování
  const bolds = tempDiv.querySelectorAll("strong, b");
  bolds.forEach((bold) => {
    bold.replaceWith(document.createTextNode(`**${bold.textContent}**`));
  });

  const italics = tempDiv.querySelectorAll("em, i");
  italics.forEach((italic) => {
    italic.replaceWith(document.createTextNode(`*${italic.textContent}*`));
  });

  // Ošetř seznamy
  const lists = tempDiv.querySelectorAll("ul, ol");
  lists.forEach((list) => {
    const items = list.querySelectorAll("li");
    let listText = "\n";
    items.forEach((item, index) => {
      const prefix = list.tagName === "UL" ? "• " : `${index + 1}. `;
      listText += `${prefix}${item.textContent}\n`;
    });
    list.replaceWith(document.createTextNode(listText));
  });

  // Ošetř nadpisy
  const headings = tempDiv.querySelectorAll("h1, h2, h3, h4, h5, h6");
  headings.forEach((heading) => {
    const level = heading.tagName.charAt(1);
    const prefix = "#".repeat(parseInt(level)) + " ";
    heading.replaceWith(
      document.createTextNode(`\n${prefix}${heading.textContent}\n`)
    );
  });

  // Získej čistý text
  let plainText = tempDiv.textContent || tempDiv.innerText || "";

  // Vyčisti formátování
  plainText = plainText
    .replace(/\n\s*\n\s*\n/g, "\n\n") // Max 2 prázdné řádky
    .replace(/[ \t]+/g, " ") // Více mezer na jednu
    .replace(/^\s+|\s+$/g, "") // Trim
    .replace(/mailto:/g, "") // Odstraň zbytky mailto:
    .replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\1+/g, "$1"); // Oprav zdvojené emaily

  return plainText;
}

// Fallback metoda s HTML podporou
async function fallbackCopyHtml(htmlContent, plainText) {
  return new Promise((resolve, reject) => {
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-9999px";
    container.style.opacity = "0";
    container.innerHTML = htmlContent;
    document.body.appendChild(container);

    const copyListener = (event) => {
      event.preventDefault();
      event.clipboardData.setData("text/html", htmlContent);
      event.clipboardData.setData("text/plain", plainText);
    };

    const range = document.createRange();
    range.selectNodeContents(container);
    window.getSelection().removeAllRanges();
    window.getSelection().addRange(range);

    document.addEventListener("copy", copyListener);

    try {
      const success = document.execCommand("copy");
      success ? resolve() : reject(new Error("Copy command failed"));
    } catch (error) {
      reject(error);
    } finally {
      document.removeEventListener("copy", copyListener);
      document.body.removeChild(container);
      window.getSelection().removeAllRanges();
    }
  });
}

// Zobrazení zpětné vazby o úspěšném kopírování
function showCopyFeedback() {
  copySuccess.value = true;
  setTimeout(() => {
    copySuccess.value = false;
  }, 2000);
}


// Otevři email s plain textem
async function sendPlainTextEmail() {
  try {
    const htmlContent = formattedHtml.value;
    const subject = await generateEmailSubject();
    const emailBody = generatePlainTextEmail(htmlContent);
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(emailBody);
    const mailtoUrl = `mailto:?subject=${encodedSubject}&body=${encodedBody}`;
    window.location.href = mailtoUrl;
    showEmailDialog.value = false;
  } catch (error) {
    console.error("Chyba při otevírání emailu:", error);
    alert("Nepodařilo se otevřít emailovou aplikaci.");
  }
}

// Vygeneruj předmět emailu na základě obsahu
async function generateEmailSubject() {
  try {
    // Vezmi prvních 500 znaků z textu pro analýzu
    const textPreview = props.text.substring(0, 500);

    const response = await fetch('/api/generate-subject', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text: textPreview })
    });

    if (!response.ok) {
      throw new Error('Failed to generate subject');
    }

    const data = await response.json();
    return data.subject || "Informace o tepelných čerpadlech HOTJET";
  } catch (error) {
    console.error("Chyba při generování předmětu:", error);
    // Fallback - pokus se odvodit z obsahu
    if (props.text.toLowerCase().includes('nabídka') || props.text.toLowerCase().includes('cena')) {
      return "Nabídka tepelných čerpadel HOTJET";
    } else if (props.text.toLowerCase().includes('výpočet') || props.text.toLowerCase().includes('kalkulace')) {
      return "Výpočet tepelného čerpadla HOTJET";
    } else if (props.text.toLowerCase().includes('srovnání')) {
      return "Srovnání tepelných čerpadel HOTJET";
    }
    return "Informace o tepelných čerpadlech HOTJET";
  }
}

// Vytvoř Gmail draft pomocí Gmail API
async function createGmailDraft() {
  try {
    // Vygeneruj předmět podle obsahu
    const subject = await generateEmailSubject();
    const cleanEmailHtml = createEmailHtml(formattedHtml.value);

    // 1. Zkontroluj zda má uživatel Gmail oprávnění
    if (!hasGmailAuth()) {
      const confirmAuth = confirm(
        "📬 Pro vytvoření draftu v Gmailu potřebujeme oprávnění.\n\n" +
        "Klikněte OK pro povolení přístupu ke Gmailu.\n\n" +
        "Poznámka: Budete přesměrováni na Google pro udělení oprávnění."
      );

      if (!confirmAuth) {
        showEmailDialog.value = false;
        return;
      }

      // Požádej o Gmail oprávnění
      try {
        await requestGmailAuth();
      } catch (authError) {
        console.error("Chyba při autorizaci:", authError);
        alert("❌ Nepodařilo se získat oprávnění pro Gmail.\n\n" + authError.message);
        showEmailDialog.value = false;
        return;
      }
    }

    // 2. Získej access token
    const accessToken = getGmailAccessToken();
    if (!accessToken) {
      alert("❌ Chybí přístupový token. Zkuste to prosím znovu.");
      showEmailDialog.value = false;
      return;
    }

    // 3. Vytvoř draft v Gmailu
    const draftResponse = await createDraft(accessToken, subject, cleanEmailHtml);


    // 4. Otevři Gmail drafts folder
    // Gmail web UI má problém s přímým otevřením draftu přes API ID
    // Místo toho otevřeme drafts folder, kde uživatel uvidí nově vytvořený draft nahoře
    const gmailDraftsUrl = `https://mail.google.com/mail/u/0/#drafts`;
    window.open(gmailDraftsUrl, '_blank');

    showEmailDialog.value = false;

    // 5. Zobraz úspěšnou zprávu
    setTimeout(() => {
      alert("✅ Draft vytvořen v Gmailu!\n\n💡 Gmail byl otevřen ve složce Koncepty.\nNajdete tam nově vytvořený draft s předmětem:\n\"" + subject + "\"\n\nOtevřete ho, doplňte příjemce a odešlete.");
    }, 500);


  } catch (error) {
    console.error("Chyba při vytváření Gmail draftu:", error);

    // Fallback - zkopíruj do schránky
    if (confirm("❌ Nepodařilo se vytvořit draft v Gmailu.\n\nChcete zkopírovat obsah do schránky a otevřít prázdný email?")) {
      await copyToClipboard();
      const subject = await generateEmailSubject();
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(subject)}`;
      window.open(gmailUrl, '_blank');

      setTimeout(() => {
        alert("✅ HTML obsah zkopírován!\n\nVložte pomocí Ctrl+V nebo Cmd+V do Gmailu.");
      }, 300);
    }

    showEmailDialog.value = false;
  }
}

// Nová funkce pro plain text email - vylepšená verze
function generatePlainTextEmail(html) {
  if (!html) return "";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;


  // 1. Odstraň citace a reference
  tempDiv.innerHTML = tempDiv.innerHTML.replace(/\[\d+\]/g, "");
  const sups = tempDiv.querySelectorAll("sup");
  sups.forEach((sup) => {
    if (/^\[\d+\]$/.test(sup.textContent.trim())) {
      sup.remove();
    }
  });

  // 2. Zpracuj tabulky FIRST (před dalšími transformacemi)
  const tables = tempDiv.querySelectorAll("table");
  tables.forEach((table) => {
    let tableText = "\n\n";
    const rows = table.querySelectorAll("tr");

    rows.forEach((row, rowIndex) => {
      const cells = row.querySelectorAll("th, td");
      const cellTexts = Array.from(cells).map((cell) => {
        // Zachovej formatting uvnitř buněk
        return cell.textContent.trim();
      });

      // Join cells with | separator
      tableText += cellTexts.join(" | ") + "\n";

      // Přidej separator po header řádku
      if (rowIndex === 0 && row.querySelector("th")) {
        const separatorParts = cellTexts.map(() => "---");
        tableText += separatorParts.join(" | ") + "\n";
      }
    });

    tableText += "\n";

    // Replace table with formatted text
    const textNode = document.createTextNode(tableText);
    table.replaceWith(textNode);
  });

  // 3. Zpracuj seznamy
  const lists = tempDiv.querySelectorAll("ul, ol");
  lists.forEach((list) => {
    const items = list.querySelectorAll("li");
    let listText = "\n";
    items.forEach((item, index) => {
      const prefix = list.tagName === "OL" ? `${index + 1}. ` : "• ";
      listText += prefix + item.textContent.trim() + "\n";
    });
    listText += "\n";
    list.replaceWith(document.createTextNode(listText));
  });

  // 4. Zpracuj nadpisy (h1-h6)
  const headings = tempDiv.querySelectorAll("h1, h2, h3, h4, h5, h6");
  headings.forEach((heading) => {
    const level = parseInt(heading.tagName.charAt(1));
    const prefix = level <= 2 ? "\n\n" : "\n";
    const suffix = "\n";
    const text = heading.textContent.trim().toUpperCase();
    heading.replaceWith(document.createTextNode(prefix + text + suffix));
  });

  // 5. Zpracuj odkazy - zachovej jen text
  const links = tempDiv.querySelectorAll("a");
  links.forEach((link) => {
    const href = link.getAttribute("href");
    let text = link.textContent;

    if (href && href.startsWith("mailto:")) {
      const email = href.replace("mailto:", "");
      if (text.includes(email + email)) {
        text = email;
      } else if (!text.includes("@")) {
        text = email;
      }
    }
    link.replaceWith(document.createTextNode(text));
  });

  // 6. Zpracuj odstavce
  const paragraphs = tempDiv.querySelectorAll("p");
  paragraphs.forEach((p) => {
    p.replaceWith(document.createTextNode(p.textContent.trim() + "\n\n"));
  });

  // 7. Zpracuj <br> tagy
  const brs = tempDiv.querySelectorAll("br");
  brs.forEach((br) => {
    br.replaceWith(document.createTextNode("\n"));
  });

  // 8. Získej finální text
  let plainText = tempDiv.textContent || tempDiv.innerText || "";

  // 9. Cleanup
  plainText = plainText
    .replace(/\n{3,}/g, "\n\n") // Max 2 consecutive newlines
    .replace(/[ \t]+/g, " ")     // Normalize spaces
    .trim();

  return plainText;
}
</script>

<style scoped>
/* Vylepšení pro copy tlačítko */
.group:hover .group-hover\:scale-110 {
  transform: scale(1.1);
}

/* Zajištění, že tooltip nebude interferovat s klikáním */
.pointer-events-none {
  pointer-events: none;
}
</style>
